import { formatDateNoEra } from "~/common/datetime";
import { getTimeZone } from "~/features/meetings/calendar-utils";

/** ISO-8601 with Z or numeric offset — otherwise treated as UTC (server stores utcnow). */
const HAS_TZ_SUFFIX = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

/**
 * Parses API datetimes (UTC) for display in the browser's local timezone
 * (same approach as clinical expert meetings / {@link getTimeZone}).
 */
export function parseServerDateTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  let normalized = trimmed.includes(" ") && !trimmed.includes("T") ? trimmed.replace(" ", "T") : trimmed;
  if (!HAS_TZ_SUFFIX.test(normalized)) {
    normalized = `${normalized}Z`;
  }

  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Calendar day in the user's timezone (YYYY-MM-DD). */
export function localDateKey(d: Date, timeZone: string = getTimeZone()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Whole-day difference between today and `iso` in the user's timezone. */
export function localCalendarDaysAgo(iso: string | null | undefined, timeZone: string = getTimeZone()): number | null {
  const d = parseServerDateTime(iso);
  if (!d) return null;

  const todayKey = localDateKey(new Date(), timeZone);
  const thenKey = localDateKey(d, timeZone);
  const [y1, m1, d1] = todayKey.split("-").map(Number);
  const [y2, m2, d2] = thenKey.split("-").map(Number);
  const todayUtc = Date.UTC(y1, m1 - 1, d1);
  const thenUtc = Date.UTC(y2, m2 - 1, d2);
  return Math.round((todayUtc - thenUtc) / 86400000);
}

function timeFormatOptions(timeZone: string): Intl.DateTimeFormatOptions {
  return { timeZone, hour: "2-digit", minute: "2-digit", hour12: false };
}

export function formatChatBubbleTime(iso: string | null | undefined, locale: string): string {
  const d = parseServerDateTime(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(locale, timeFormatOptions(getTimeZone())).format(d);
}

export function formatChatThreadListTime(iso: string | null | undefined, locale: string): string {
  const d = parseServerDateTime(iso);
  if (!d) return "";

  const diffDays = localCalendarDaysAgo(iso);
  if (diffDays === null) return "";
  if (diffDays === 0) {
    return formatChatBubbleTime(iso, locale);
  }
  if (diffDays === 1) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(-1, "day");
  }
  return new Intl.DateTimeFormat(locale, {
    timeZone: getTimeZone(),
    day: "numeric",
    month: "short",
  }).format(d);
}

export function messageDayKey(iso: string | null | undefined): string {
  const d = parseServerDateTime(iso);
  if (!d) return "";
  return localDateKey(d);
}

export function formatChatDateSeparator(iso: string | null | undefined, locale: string): string {
  const d = parseServerDateTime(iso);
  if (!d) return "";

  const diffDays = localCalendarDaysAgo(iso);
  if (diffDays === null) return "";
  if (diffDays === 0) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(0, "day");
  }
  if (diffDays === 1) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(-1, "day");
  }

  const now = new Date();
  const nowYear = new Intl.DateTimeFormat(locale, { timeZone: getTimeZone(), year: "numeric" }).format(now);
  const thenYear = new Intl.DateTimeFormat(locale, { timeZone: getTimeZone(), year: "numeric" }).format(d);

  return new Intl.DateTimeFormat(locale, {
    timeZone: getTimeZone(),
    day: "numeric",
    month: "long",
    ...(thenYear !== nowYear ? { year: "numeric" as const } : {}),
  }).format(d);
}

/** Stable YYYY-MM key for grouping chat files by month (server UTC → local month). */
export function chatFileMonthKey(iso: string | null | undefined): string {
  const d = parseServerDateTime(iso);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: getTimeZone(), year: "numeric", month: "2-digit" }).formatToParts(d);
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  return `${year}-${month}`;
}

/** The month pill label for the chat-files view — "Июль" / "Июль 2025" when not the current year (frame 5289-123835). */
export function formatChatFileMonth(iso: string | null | undefined, locale: string): string {
  const d = parseServerDateTime(iso);
  if (!d) return "";
  const now = new Date();
  const nowYear = new Intl.DateTimeFormat(locale, { timeZone: getTimeZone(), year: "numeric" }).format(now);
  const thenYear = new Intl.DateTimeFormat(locale, { timeZone: getTimeZone(), year: "numeric" }).format(d);
  // The month pill omits the era literal appended by the Russian long-date format.
  const label = formatDateNoEra(
    new Intl.DateTimeFormat(locale, {
      timeZone: getTimeZone(),
      month: "long",
      ...(thenYear !== nowYear ? { year: "numeric" as const } : {}),
    }).formatToParts(d),
  );
  return label.charAt(0).toUpperCase() + label.slice(1);
}
